// Import necessary components and hooks
import { Card, Form, Select, Flex, InputNumber, Row, Col, Switch, Button, Image, Tooltip, message } from "antd";
import { themeState } from "../lib/atom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { FaBan } from "react-icons/fa6";
import { PiGridNineFill } from "react-icons/pi";
import React, { useEffect, useState } from 'react';
import { deletePanelIndex, panelsOK, polygonDetailsSelector, selectedIndicesState, solarPanel } from "../ThreeJs/ReactStates/states";
import { sceneManager } from '../ThreeJs/SetUps/SceneManager';
import { autoPopulateSegment } from '../ThreeJs/Panels/AutoPopulate';
import { DrawingModes } from "../ThreeJs/Global/variables";
import { DeleteOutlined, DownOutlined, InfoCircleOutlined, PlusCircleFilled, } from "@ant-design/icons";
import { deselectPanels, selectAllPanels } from "../ThreeJs/Panels/PanelFunctions";
import InfoModal from "./InfoModal";
import magic from "../assets/magic.svg";
import { addPanelSelector } from "../ThreeJs/ReactStates/updates";

// Main component for managing solar panel settings
const SolarPanelSettings = ({ updateDrawingMode, setShowPanelSettings, deleteConfirmation, panelList }) => {
	const theme = useRecoilState(themeState)[0]; // Get current theme (dark/light)
	const details = useRecoilValue(polygonDetailsSelector); // Polygon metadata like azimuth and pitch
	const [manual, setManual] = useState(true); // State for manual placement toggle
	const [modalOpen, setModalOpen] = useState(false); // State for info modal visibility
	const [isLoading, setIsLoading] = React.useState(false);
	const [panel, setSolarPanel] = useRecoilState(solarPanel); // Currently selected panel data
	const [arePanelsOK, setPanelsOK] = useState(panelsOK);
	const [selectedIndices, setSelectedIndices] = useRecoilState(selectedIndicesState); // Selected panel indices
	const setDeletePanels = useSetRecoilState(deletePanelIndex); // Recoil setter for panels to delete
	const addPanel = useSetRecoilState(addPanelSelector);
	const { panelIndex } = selectedIndices; // Extract selected panel indices

	// Generate Select dropdown options from panel list
	const options = panelList.map(panel => {
		const img = sessionStorage.getItem(panel.id.toString()); // Retrieve stored image
		return {
			value: panel.id,
			label: (
				<Flex gap='small' style={{ alignItems: 'center' }}>
					<Image src={img} width={30} id={panel.id.toString()} />
					{panel.name}
				</Flex>
			)
		}
	});

	// Default values for the form
	const initialVals = {
		id: panel.id ?? panelList[0].id,
		azimuth: panel.azimuth ?? details.azimu,
		tilt:0,
		height: 3,
		racking: panel.racking ?? "Flush",
		orientation: panel.orientation ?? "Portrait",
		moduleSpacingCol: panel.moduleSpacingCol ?? 1,
		moduleSpacingRow: panel.moduleSpacingRow ?? 1,
		arraySpacingCol: panel.arraySpacingCol ?? 0,
		arraySpacingRow: panel.arraySpacingRow ?? 0,
		arrayLayoutCol: panel.arrayLayoutCol ?? 1,
		arrayLayoutRow: panel.arrayLayoutRow ?? 1
	};

	// Initialize panel state with form values once on mount
	useEffect(() => {
		setSolarPanel(initialVals);
	}, []);

	// Handle form field changes
	const handleChange = (values) => {
		setSolarPanel(prevState => { return { ...prevState, ...values } });
		updateDrawingMode(DrawingModes.NONE);  // Stop any current drawing mode
	}

	// Toggle between manual and automatic panel placement
	const handleToggle = (checked) => {
		if (checked) {
			setManual(true);
		} else {
			setManual(false);
			updateDrawingMode(DrawingModes.NONE);
		}
	}

	// Activate drawing mode for manually adding new panels
	const handleAdd = () => {
		updateDrawingMode(DrawingModes.PANEL);
		document.getElementById("pulse-button").style.animation = "none";
	}

	// Select all panels in the scene
	const handleSelect = () => {
		selectAllPanels(selectedIndices, setSelectedIndices, setShowPanelSettings);
	}

	// Deselect currently selected panels
	const handleDeselect = () => {
		deselectPanels(panelIndex, setSelectedIndices, selectedIndices);
	}

	// Mark panels for deletion
	const handleDelete = () => {
		setDeletePanels(panelIndex);
		deleteConfirmation(true); // Trigger confirmation modal
	}

	const handleAutoPopulate = async () => {

		try {
			setIsLoading(true);
			message.loading({ content: 'Auto-populating panels...', key: 'autopopulate', duration: 0 });

			// Call the auto-populate function with default options
			const result = await autoPopulateSegment(
				selectedIndices.segmentIndex,
				panel,
				addPanel,
				panelList,
				sceneManager.scene,
				sceneManager.camera,
				setPanelsOK,
				arePanelsOK
			);

			message.success({
				content: `Successfully placed ${result.panelsPlaced} panels`,
				key: 'autopopulate',
				duration: 3
			});

			// Show more detailed stats as a notification
			if (result.panelsPlaced > 0) {
				message.info(
					`Placed ${result.panelsPlaced} out of ${result.totalPositions} possible positions`,
					4
				);
			} else {
				message.warning(
					'Could not place any panels. The segment may be too small or have too many obstacles.',
					4
				);
			}
		} catch (error) {
			console.error('Error during auto-populate:', error);
			message.error({
				content: 'Failed to auto-populate panels',
				key: 'autopopulate',
				duration: 3
			});
		} finally {
			setIsLoading(false);
		}
	};

	// Label element with info icon and tooltip
	const label = (
		<Flex justify="space-between" style={{ width: '100%' }}>
			<>Choose Module</>
			{panel.id !== null && <Tooltip title="Module Information">
				<InfoCircleOutlined
					className="info"
					onClick={() => setModalOpen(true)}
				/>
			</Tooltip>}
		</Flex>
	)	

	const darkIcon = (
		<DownOutlined
			style={{ color: '#ccc' }}
		/>
	)

	return (
		<div>
			{/* Main settings card */}
			<Card style={{ fontSize: '16px', maxWidth: '275px' }} bodyStyle={{ padding: 0, fontSize: '16px' }} className='segment__card'>
				<Form
					layout="vertical"
					style={{ padding: '10px', height: '275px', overflow: 'auto', overflowX: 'hidden' }}
					className={` ${theme ? 'custom-scrollbar-dark' : 'custom-scrollbar'}`}
					initialValues={initialVals}
					onValuesChange={handleChange}
				>
					<Row justify='space-between' gutter={[16, 10]} align='middle'>
						{/* Module selection dropdown */}
						<Col span={24}>
							<Form.Item label={label} name="id">
								<Select
									suffixIcon={theme ? darkIcon : <DownOutlined />}
									options={options}
									optionLabelProp="label"
									placement="bottomRight"
									size="large"
									variant={theme ? 'outlined' : 'filled'}
									dropdownStyle={theme ? { backgroundColor: 'black', color: 'white' } : {}}
									style={{ height: 'auto', alignItems: 'center' }}
								/>
							</Form.Item>
						</Col>
						{/* Azimuth, tilt, and height inputs */}
						<Col span={8}>
							<Form.Item label="Azimuth(&deg;)" name="azimuth">
								<InputNumber disabled />
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item label="Tilt(&deg;)" name="tilt">
								<InputNumber />
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item label="Height(in)" name="height">
								<InputNumber disabled />
							</Form.Item>
						</Col>
						{/* Racking and orientation options */}
						<Col span={12}>
							<Form.Item label="Racking" flex={2} name="racking">
								<Select
									suffixIcon={theme ? darkIcon : <DownOutlined />}
									options={[{ value: "Flush" }, { value: "Tilted", disabled: true }, { value: "Double Tilt", disabled: true }, { value: "Ground", disabled: true }]}
									dropdownStyle={theme && { backgroundColor: 'black', color: "white" }}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Orientation" flex={3} name="orientation">
								<Select
									suffixIcon={theme ? darkIcon : <DownOutlined />}
									options={[{ value: "Landscape" }, { value: "Portrait" }]}
									dropdownStyle={theme && { backgroundColor: 'black', color: "white" }}
								/>
							</Form.Item>
						</Col>
						{/* Commented out spacing/layout fields (for use with auto population functionality) */}
						{/* Module/array spacing and layout fields */}
						{/* <Col span={12}><p style={{ marginTop: '11px' }}>Module Spacing (in)</p></Col>
						<Col span={6}>
							<Form.Item label="Col" name="moduleSpacingCol">
								<InputNumber min={0} disabled />
							</Form.Item>
						</Col>
						<Col span={6}>
							<Form.Item label="Row" name="moduleSpacingRow">
								<InputNumber min={0} disabled />
							</Form.Item>
						</Col>
						<Col span={12}>Array Spacing (in)</Col>
						<Col span={6}>
							<Form.Item name="arraySpacingCol">
								<InputNumber min={0} disabled />
							</Form.Item>
						</Col>
						<Col span={6}>
							<Form.Item name="arraySpacingRow">
								<InputNumber min={0} disabled />
							</Form.Item>
						</Col>
						<Col span={12}>Array Layout (modules)</Col>
						<Col span={6}>
							<Form.Item name="arrayLayoutCol">
								<InputNumber min={0} disabled />
							</Form.Item>
						</Col>
						<Col span={6}>
							<Form.Item name="arrayLayoutRow">
								<InputNumber min={0} disabled />
							</Form.Item>
						</Col> */}
						{/* Manual mode toggle */}
						<Col span={16}>Manually Add Panels</Col>
						<Col span={6} offset={2}>
							<Switch defaultChecked onChange={(checked) => { handleToggle(checked) }} disabled={panel.id === null} />
						</Col>
						{/* Action buttons: Add, select, deselect, delete */}
						<Col span={3}>
							<Tooltip title='Add new module'>
								<Button disabled={!manual} icon={<PlusCircleFilled />} onClick={handleAdd} id={`${theme ? 'pulse-button-dark' : 'pulse-button'}`}/>
							</Tooltip>
						</Col>
						<Col span={3}>
							<Tooltip title='Select all modules'>
								<Button icon={<PiGridNineFill fontSize={20} />} onClick={handleSelect} className={`${theme ? 'dark' : 'light'}`}/>
							</Tooltip>
						</Col>
						<Col span={3}>
							<Tooltip title='Deselect all modules'>
								<Button disabled={panelIndex === null || panelIndex.length === 0} icon={<FaBan />} onClick={handleDeselect} className={`${theme ? 'dark' : 'light'}`}/>
							</Tooltip>
						</Col>
						<Col span={3}>
							<Tooltip title='Delete selected module(s)'>
								<Button danger disabled={panelIndex === null || panelIndex.length === 0} icon={<DeleteOutlined />} onClick={handleDelete} className={`${theme ? 'dark' : 'light'}`}/>
							</Tooltip>
						</Col>
						<Col span={8} />
					</Row>
				</Form>
				<Button
					block
					type="primary"
					// icon={<Icon src={magic} />}
					// iconPosition="end"
					className={`${!manual && "gray-to-white"}`}
					onClick={handleAutoPopulate}
					loading={isLoading}
					// disabled
					style={{ marginTop: '5px' }}
				>
					Auto Fill Modules
					<img src={magic} style={{ marginLeft: '10px', width: '15px', color: 'white' }} />
				</Button>
			</Card>
			{/* Show info modal if a panel is selected */}
			{panel.id !== null && <InfoModal
				open={modalOpen}
				setOpen={setModalOpen}
				panelInfo={panel}
				panelList={panelList}
			/>}
		</div>
	)
}

export default SolarPanelSettings;